import { useAuthStore, useAuthSelectors } from '../authStore';

/**
 * Custom hook for authentication-related operations
 * Provides convenient access to auth state and actions
 */
export const useAuth = () => {
  const {
    isAuthenticating,
    authError,
    authorization,
    groups,
    user,
    userApiKey,
    userType,
    login,
    logout,
    setUser,
    setAuthorization,
    setGroups,
    setUserApiKey,
    setAuthError,
    setUserType,
    initializeAuth,
    fetchUserType,
  } = useAuthStore();

  const { isAuthenticated, currentUser, isAcademic, isTrial, isPremium } = useAuthSelectors();

  return {
    // State
    isAuthenticated,
    isAuthenticating,
    authError,
    authorization,
    groups,
    user,
    userApiKey,
    userType,
    currentUser,

    // Computed values
    isAcademic,
    isTrial,
    isPremium,
    isLoggedIn: isAuthenticated,
    hasRole: (role: string) => groups.includes(role),
    getUserRole: () => groups?.[0] || 'user',

    // Actions
    login,
    logout,
    setUser,
    setAuthorization,
    setGroups,
    setUserApiKey,
    setAuthError,
    setUserType,
    initializeAuth,
    fetchUserType,

    // Convenience methods
    canAccessFeature: (feature: 'premium' | 'academic' | 'trial') => {
      if (!isAuthenticated) return false;
      
      switch (feature) {
        case 'premium':
          return isPremium;
        case 'academic':
          return isAcademic;
        case 'trial':
          return isTrial;
        default:
          return false;
      }
    },

    getUserDisplayName: () => {
      if (!user) return null;
      return user.username || user.email || user.sub || 'User';
    },
  };
};

/**
 * Hook for checking authentication status only
 * Useful when you only need to know if user is authenticated
 */
export const useAuthStatus = () => {
  const isAuthenticated = useAuthStore((state) => !!state.user);
  const isAuthenticating = useAuthStore((state) => state.isAuthenticating);
  
  return {
    isAuthenticated,
    isAuthenticating,
    isReady: !isAuthenticating,
  };
};

/**
 * Hook for user information only
 * Useful when you only need user data
 */
export const useUser = () => {
  const user = useAuthStore((state) => state.user);
  const userType = useAuthStore((state) => state.userType);
  const groups = useAuthStore((state) => state.groups);
  const { currentUser } = useAuthSelectors();
  
  return {
    user,
    userType,
    groups,
    currentUser,
  };
};

/**
 * Hook for permission checks
 * Useful for conditional rendering based on user permissions
 */
export const usePermissions = () => {
  const { isAcademic, isTrial, isPremium } = useAuthSelectors();
  const isAuthenticated = useAuthStore((state) => !!state.user);
  const groups = useAuthStore((state) => state.groups);

  return {
    isAuthenticated,
    isAcademic,
    isTrial,
    isPremium,
    hasRole: (role: string) => groups.includes(role),
    canAccess: {
      premiumFeatures: isPremium,
      academicFeatures: isAcademic,
      trialFeatures: isTrial,
      authenticatedFeatures: isAuthenticated,
    },
  };
};